import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import RegisterForm from "./RegisterForm";
import { registerUserViaNetlify } from "../registerUserViaNetlify";

// --- Identifier Pipeline (repeat in frontend as per current plan) ---

/**
 * Checks if the identifier is already a valid email.
 * If not, transforms it to a dummy email (e.g., user123 → user123@dummy.com).
 * @param {string} identifier - The input username or email.
 * @returns {string} - A valid email address.
 */
function transformToEmail(identifier) {
  const emailRegex = /^([a-z0-9._-]+)@([a-z0-9.-]+\.[a-z]{2,})$/;
  if (emailRegex.test(identifier)) {
    return identifier;
  }
  return `${identifier}@dummy.com`;
}

/**
 * Prepares the identifier for authentication.
 * - Trims whitespace and converts to lowercase.
 * - Transforms to a valid email if necessary.
 * @param {string} input - Raw user input (username or email).
 * @returns {string} - Prepared email for registration/login.
 */
function prepareIdentifier(input) {
  const normalized = input.trim().toLowerCase();
  // (Optional) Add blacklist/validation here if needed
  return transformToEmail(normalized);
}

// --- End Identifier Pipeline ---

console.log("Register component rendered");

/**
 * Registration page component.
 * Handles user registration, promo code confirmation, and user type assignment.
 */
function Register() {
  const navigate = useNavigate();

  // State for error messages
  const [registerError, setRegisterError] = useState("");
  // State to indicate if the form is submitting
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Handles registration form submission.
   * @param {Object} formData - The form data: { name, username, password, promocode }
   */
  const submitRegister = async ({ name, username, password, promocode }) => {
    setRegisterError(""); // Clear any previous errors
    setIsSubmitting(true); // Indicate submission in progress

    try {
      // 1. If promo code is empty, confirm with user before proceeding
      if (!promocode) {
        const result = await Swal.fire({
          title: "Promo Code",
          text: "Are you sure you want to proceed without entering a promo code?",
          icon: "info",
          showCancelButton: true,
          confirmButtonText: "OK",
          cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) {
          setIsSubmitting(false);
          return;
        }
      }

      // 2. Use default promo code if none provided
      const promoToUse = promocode === "" ? "DE100" : promocode;

      // 3. Prepare the username (transform to email if needed)
      let preparedUsername;
      try {
        preparedUsername = prepareIdentifier(username);
      } catch (err) {
        setRegisterError(err.message || "Invalid username.");
        setIsSubmitting(false);
        return;
      }

      // 4. Call the Netlify Function to register user securely
      const result = await registerUserViaNetlify({
        name,
        username: preparedUsername, // Use the prepared identifier
        password,
        promocode: promoToUse,
      });

      if (result.success) {
        await Swal.fire({
          icon: "success",
          title: "Registration Successful",
          text: `You have been registered${
            result.assignedType ? ` as ${result.assignedType}` : ""
          }. Please log in.`,
          confirmButtonText: "OK",
        });
        navigate("/login");
      } else {
        setRegisterError(
          result.error || "Registration failed. Please try again."
        );
      }
    } catch (error) {
      setRegisterError(error.message || "Something went wrong!");
    } finally {
      setIsSubmitting(false); // Reset submitting state
    }
  };

  return (
    <div className="container-fluid d-flex align-items-center justify-content-center">
      <div style={{ width: "350px", marginTop: "100px" }}>
        {/* Registration form component */}
        <RegisterForm
          registerError={registerError}
          isSubmitting={isSubmitting}
          onSubmit={submitRegister}
        />
      </div>
    </div>
  );
}

export default Register;
